import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Input,
  Select,
  Button,
  message,
  Checkbox,
  Alert,
  Tooltip,
  Spin,
} from 'antd';
import ActiveStatusFormItem from 'app/shared/components/Form/ActiveStatusFormItem';

import {
  Fee,
  BookingSystem
} from 'app/shared/model';

import {
  getDraftFeeProduct,
  getFormErrors,
  getUnitTypes,
  getFeeById,
  getFeesProductsData,
} from '../selectors';
import {
  setFormErrors,
  UpdateDraftFeeProduct,
  loadDraftFeeProduct,
} from '../actions';
import * as api from 'app/shared/api';
import { useActivePackageDeactivationLock } from '../hooks/useActivePackageDeactivationLock';
import { PRODUCT_ACTIVE_PACKAGE_TOOLTIP } from 'app/shared/api/productDtoHelpers';

interface FeeFormOwnProps {
  feeProductId?: number;
  editMode?: boolean;
  onSuccess?: () => void | Promise<void>;
}

const FEE_PRODUCT_TYPE_ID = 6;
const UNIT_TYPE_FEE_PRODUCT_TYPE_ID = 98;

const AddEditFeeForm: React.FC<FeeFormOwnProps> = ({ feeProductId, editMode, onSuccess }) => {
  const dispatch = useDispatch<any>();
  const draftProduct = useSelector(getDraftFeeProduct) as Fee | undefined;
  const formErrors = useSelector(getFormErrors);
  const unitTypes = useSelector(getUnitTypes) || [];
  const allFees = useSelector(getFeesProductsData) || [];
  const feeFromSelectorArr = useSelector((state: any) =>
    feeProductId != null ? getFeeById(state, feeProductId) : []
  );

  const isNew = !editMode && (feeProductId == null || feeProductId <= 0);

  const reduxFeeProduct = useMemo(() => {
    if (isNew) return undefined;
    const feeFromList = feeProductId
      ? allFees.find(f => Number(f.id) === Number(feeProductId))
      : undefined;
    const feeFromSelector = Array.isArray(feeFromSelectorArr) ? feeFromSelectorArr[0] : undefined;
    return feeFromList || feeFromSelector;
  }, [isNew, allFees, feeProductId, feeFromSelectorArr]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string>('');
  const [bookingSystems, setBookingSystems] = useState<BookingSystem[]>([]);
  const [isExclusionLoading, setIsExclusionLoading] = useState(false);
  const [initialExcludedSystems, setInitialExcludedSystems] = useState<string[]>([]);
  const [feeFetchedById, setFeeFetchedById] = useState<Fee | undefined>(undefined);
  const [feeDetailLoading, setFeeDetailLoading] = useState(() =>
    Boolean(editMode && feeProductId != null && feeProductId > 0)
  );
  const unitTypeOptions = useMemo(
    () =>
      (unitTypes || [])
        .filter((u) => u.id > 0)
        .filter((u) =>
          (u.allowedProductTypes ?? []).some((pt) => {
            const name = String((pt as any)?.name ?? "").trim().toLowerCase();
            const id = Number((pt as any)?.id);
            return name === "fee" || id === UNIT_TYPE_FEE_PRODUCT_TYPE_ID;
          })
        )
        .map((u) => ({ label: u.name, value: Number(u.id) })),
    [unitTypes]
  );
  const getUnitTypeName = (id?: number) => {
    if (id == null) return '';
    const m = (unitTypes || []).find(u => Number(u.id) === Number(id));
    return m?.name ?? '';
    };

  const feeProduct = useMemo(() => {
    const base = reduxFeeProduct;
    if (!feeFetchedById) {
      return base;
    }
    return {
      ...base,
      ...feeFetchedById,
      entityUpdate: feeFetchedById.entityUpdate ?? base?.entityUpdate,
      lastModifiedDate: feeFetchedById.lastModifiedDate ?? base?.lastModifiedDate,
      lastUpdatedBy: feeFetchedById.lastUpdatedBy ?? base?.lastUpdatedBy,
      isUsedInActivePackage:
        typeof feeFetchedById.isUsedInActivePackage === 'boolean'
          ? feeFetchedById.isUsedInActivePackage
          : base?.isUsedInActivePackage,
    };
  }, [feeFetchedById, reduxFeeProduct]);

  useEffect(() => {
    if (isNew || feeProductId == null || feeProductId <= 0) {
      setFeeFetchedById(undefined);
      setFeeDetailLoading(false);
      return;
    }
    let cancelled = false;
    setFeeDetailLoading(true);
    setFeeFetchedById(undefined);
    api
      .getFeeById(String(feeProductId))
      .then(fetched => {
        if (!cancelled && fetched) {
          setFeeFetchedById(fetched);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          message.error(err?.message ?? 'Failed to load fee');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setFeeDetailLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isNew, feeProductId]);

  useEffect(() => {
    let isMounted = true;
    api.getAllBookingSystems()
      .then(systems => {
        if (isMounted) {
          setBookingSystems(systems);
        }
      })
      .catch((err: any) => {
        message.error(err?.message ?? 'Failed to load booking systems');
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (isNew) {
      dispatch(loadDraftFeeProduct(undefined));
      dispatch(UpdateDraftFeeProduct(undefined));
      setInitialExcludedSystems([]);
      form.resetFields();
      form.setFieldsValue({
        name: undefined,
        id: undefined,
        unitTypeId: undefined,
        active: true,
      excludedBookingSystemIds: [],
      });
      return;
    }

    form.setFieldsValue({
      name: draftProduct?.name ?? feeProduct?.name,
      id: feeProduct?.id,
      active: draftProduct?.active ?? feeProduct?.active ?? true,
      excludedBookingSystemIds: draftProduct?.excludedBookingSystemIds
        ?? feeProduct?.excludedBookingSystemIds
        ?? [],
    });

    if (!unitTypeOptions.length) return;

    const fromDraft = draftProduct?.unitTypeId ?? feeProduct?.unitTypeId;
    const value = fromDraft != null ? Number(fromDraft) : undefined;
    const exists = value != null && unitTypeOptions.some(o => o.value === value);
    form.setFieldsValue({ unitTypeId: exists ? value : undefined });
  }, [
    isNew,
    form,
    unitTypeOptions,
    feeProduct?.id,
    feeProduct?.name,
    feeProduct?.unitTypeId,
    feeProduct?.active,
    draftProduct?.name,
    draftProduct?.unitTypeId,
    draftProduct?.active,
  ]);

  useEffect(() => {
    if (isNew) {
      setInitialExcludedSystems([]);
      form.setFieldsValue({ excludedBookingSystemIds: [] });
      setIsExclusionLoading(false);
      return;
    }

    const productId = feeProduct?.id ?? draftProduct?.id ?? 0;
    if (!productId) {
      setInitialExcludedSystems([]);
      setIsExclusionLoading(false);
      return;
    }

    let cancelled = false;
    setIsExclusionLoading(true);
    api.getExcludedBookingSystemIdsForProduct(productId, FEE_PRODUCT_TYPE_ID)
      .then(ids => {
        if (!cancelled) {
          setInitialExcludedSystems(ids);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setInitialExcludedSystems([]);
          message.error(err?.message ?? 'Failed to load booking system exclusions');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsExclusionLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isNew, feeProduct?.id, draftProduct?.id, form]);

  useEffect(() => {
    form.setFieldsValue({ excludedBookingSystemIds: initialExcludedSystems });
  }, [initialExcludedSystems, form]);

  useEffect(() => {
    if (formErrors?.length) {
      formErrors.forEach(err => {
        form.setFields([{ name: err.name, errors: [err.error] }]);
      });
    }
  }, [formErrors, form]);

  const formDisabled = Boolean(feeProduct?.entityUpdate);
  const unitTypeLocked =
    !formDisabled &&
    !isNew &&
    feeProduct?.hasActiveOffering === true;

  const { activeDeactivationLocked } = useActivePackageDeactivationLock(
    feeProduct,
    FEE_PRODUCT_TYPE_ID,
    0,
    feeFetchedById
  );
  const preserveActiveOnSave = activeDeactivationLocked;

  const handleValuesChange = (_changed: any, all: any) => {
    const base: Partial<Fee> = isNew ? {} : (draftProduct || feeProduct || {});
    const updated: Fee = { ...(base as Fee), ...all };
    dispatch(UpdateDraftFeeProduct(updated));
  };

  const handleSubmit = async () => {
    try {
      dispatch(setFormErrors([]));
      const values = await form.validateFields();
      setLoading(true);
      const selectedSystems = form.getFieldValue('excludedBookingSystemIds') as (string | number)[] | undefined;
      const normalizedSelected = Array.isArray(selectedSystems)
        ? selectedSystems.map(id => String(id))
        : [];
      const nowIso = new Date().toISOString();
      const lastModifiedBy = localStorage.getItem('displayName') || localStorage.getItem('userName') || 'System';
      const lastModifiedById = Number(localStorage.getItem('userId')) || 0;

      let payload: Fee;

      if (isNew) {
        payload = {
          id: 0,
          name: String(values.name || '').trim(),
          unitTypeId: Number(values.unitTypeId),
          unitTypeName: getUnitTypeName(Number(values.unitTypeId)),
          active: values.active !== false,
          lastModifiedDate: nowIso,
          lastModifiedBy,
          lastModifiedById,
          entityUpdate: false,
        } as Fee;
      } else {
        const existing = draftProduct || feeProduct || ({} as Fee);
        const unitTypeId = unitTypeLocked
          ? Number(feeProduct?.unitTypeId ?? draftProduct?.unitTypeId ?? existing.unitTypeId ?? 0)
          : Number(values.unitTypeId ?? existing.unitTypeId);
        payload = {
          ...existing,
          id: Number(existing.id),
          name: String(values.name ?? existing.name ?? '').trim(),
          unitTypeId,
          unitTypeName: getUnitTypeName(unitTypeId),
          active: values.active !== false,
          lastModifiedDate: nowIso,
          lastModifiedBy,
          lastModifiedById
        } as Fee;
      }

      const payloadToSave = { ...payload } as Fee;
      delete (payloadToSave as any).excludedBookingSystemIds;
      payloadToSave.active = preserveActiveOnSave
        ? (feeProduct?.active ?? values.active !== false)
        : values.active !== false;

      const result = await api.saveFee(payloadToSave);
      const responseShape = result as { ok?: boolean; message?: string; Errors?: any; errors?: any; successful?: boolean; data?: { id?: number } } | undefined;

      if (typeof responseShape?.ok === 'boolean' && !responseShape.ok) {
        const msg = responseShape.message || 'Failed to save fee';
        message.error(msg);
        setStatusMsg('Error ' + msg);
        return;
      }

      const rawErrors = responseShape?.Errors ?? responseShape?.errors;
      if (Array.isArray(rawErrors) && rawErrors.length) {
        const first = rawErrors[0];
        const msg = first?.error || first?.message || 'Failed to save fee';
        message.error(msg);
        setStatusMsg('Error ' + msg);
        return;
      }

      if (responseShape?.successful === false) {
        const msg = responseShape.message || 'Failed to save fee';
        message.error(msg);
        setStatusMsg('Error ' + msg);
        return;
      }

      const savedProductId = responseShape?.data?.id ?? payload.id ?? feeProduct?.id ?? 0;

      if (savedProductId > 0) {
        try {
          const updatedIds = await api.updateBookingSystemProductExclusions(
            savedProductId,
            FEE_PRODUCT_TYPE_ID,
            normalizedSelected,
            initialExcludedSystems
          );
          setInitialExcludedSystems(updatedIds);
        } catch (err: any) {
          const msg = err?.message || 'Failed to update booking system exclusions';
          message.error(msg);
          setStatusMsg('Error ' + msg);
          return;
        }
      } else if (normalizedSelected.length > 0) {
        message.warning('Saved, but booking system exclusions could not be updated because the product id was unavailable.');
      }

      message.success(isNew ? 'Fee product created!' : 'Fee product updated!');
      setStatusMsg(isNew ? 'Fee product created' : 'Fee product updated');

      dispatch(UpdateDraftFeeProduct(undefined));
      dispatch(loadDraftFeeProduct(undefined));

      if (isNew) {
        form.resetFields();
        form.setFieldsValue({ active: true, excludedBookingSystemIds: [] });
        setInitialExcludedSystems([]);
      }

      if (onSuccess) {
        await onSuccess();
      }
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      const msg = err?.message || 'Failed to save fee';
      message.error(msg);
      setStatusMsg('Error ' + msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isNew && feeDetailLoading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isNew && !feeProduct) {
    return <div>No Fee found</div>;
  }

  return (
    <Form
      layout="vertical"
      form={form}
      disabled={formDisabled}
      initialValues={{ active: true }}
      onValuesChange={handleValuesChange}
      onFinish={handleSubmit}
    >
      {formDisabled && (
        <Alert type="info" title="Publishing an update" description="This record is locked while an update is being published. Editing is disabled." showIcon style={{ marginBottom: 16 }} />
      )}
      <Form.Item
        label="Name"
        name="name"
        rules={[
          { required: true, message: 'Please enter fee name' },
          { max: 100, message: 'Max length is 100 characters' },
        ]}
      >
        <Input maxLength={100} placeholder="Enter fee name" />
      </Form.Item>

      <Form.Item label="Unit Type" required>
        <Tooltip title={unitTypeLocked ? 'Has active offering' : undefined}>
          <span style={{ display: 'block', width: '100%' }}>
            <Form.Item
              name="unitTypeId"
              noStyle
              rules={[{ required: true, message: 'Please select a unit type' }]}
            >
              <Select
                placeholder="Select Unit Type"
                options={unitTypeOptions}
                allowClear={!unitTypeLocked}
                showSearch={unitTypeLocked ? false : { optionFilterProp: 'label' }}
                disabled={formDisabled || unitTypeLocked}
              />
            </Form.Item>
          </span>
        </Tooltip>
      </Form.Item>

      <Form.Item hidden label="ID" name="id">
        <Input disabled />
      </Form.Item>

      <ActiveStatusFormItem
        name="active"
        tooltip={activeDeactivationLocked ? PRODUCT_ACTIVE_PACKAGE_TOOLTIP : undefined}
        disabled={activeDeactivationLocked}
      />
      <Form.Item
        name="excludedBookingSystemIds"
        label={
          <>
            <span style={{ fontWeight: 'bold', color: 'red', marginRight: 3 }}>Exclude</span>
            {' this fee product from the following systems:'}
          </>
        }
      >
        <Checkbox.Group
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          disabled={isExclusionLoading}
        >
          {bookingSystems.map(system => (
            <Checkbox key={system.id} value={String(system.id)}>
              {system.name}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>

      <Form.Item style={{ textAlign: 'right' }}>
        <Button type="primary" htmlType="submit" loading={loading} disabled={formDisabled} style={{ minWidth: 100 }}>
          Save
        </Button>
        <span style={{ marginLeft: 16, color: '#52c41a' }}>{statusMsg}</span>
      </Form.Item>
    </Form>
  );
};

export default AddEditFeeForm;